
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { ChatAgent } from "@/components/ChatAgent";
import { useEffect, useState } from "react";

// Pages
import Index from "./pages/Index";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import Chat from "./pages/Chat";
import PlanGeneration from "./pages/PlanGeneration";
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import Upload from "./pages/Upload";
import FAQ from "./pages/FAQ";
import NotFound from "./pages/NotFound";
import Branding from "./pages/Branding";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import BlogAdmin from "./pages/BlogAdmin";
import BlogPostEditor from "./pages/BlogPostEditor";
import InterviewBasics from "./pages/InterviewBasics";

// Admin Pages
import AdminDashboard from "./pages/AdminDashboard";
import Metrics from "./pages/Metrics";
import ConversationData from "./pages/ConversationData";
import AISettings from "./pages/AISettings";
import AdminReports from "./pages/AdminReports";
import AdminTestPipeline from "./pages/AdminTestPipeline";

const queryClient = new QueryClient();

// Protected route component for admin pages
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  
  useEffect(() => {
    const authData = localStorage.getItem('adminAuth');
    setIsAuthenticated(authData ? JSON.parse(authData).authenticated : false);
  }, []);
  
  if (isAuthenticated === null) {
    // Still checking authentication
    return null;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ isAdmin: true }} replace />;
  }
  
  return <>{children}</>;
};

// ChatAgent wrapper that conditionally renders based on route
const ConditionalChatAgent = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  // Don't disable it on the homepage anymore - it should appear on all pages
  // This was causing the chat bubble to disappear
  const disabledPaths = []; // Previously was ["/"]
  
  if (disabledPaths.includes(currentPath)) {
    return null;
  }
  
  return <ChatAgent />;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner position="bottom-right" expand={false} closeButton duration={3000} />
        <BrowserRouter>
          <AnimatePresence mode="wait">
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/interview/basics" element={<InterviewBasics />} />
              <Route path="/chat" element={<Chat />} />
              <Route path="/plan-generation/:interviewId" element={<PlanGeneration />} />
              <Route path="/features" element={<Features />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/about" element={<About />} />
              <Route path="/upload" element={<Upload />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/branding" element={<Branding />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              
              {/* Admin Routes - Protected */}
              <Route path="/admin-dashboard" element={
                <AdminRoute><AdminDashboard /></AdminRoute>
              } />
              <Route path="/blog-admin" element={
                <AdminRoute><BlogAdmin /></AdminRoute>
              } />
              <Route path="/blog-post-editor" element={
                <AdminRoute><BlogPostEditor /></AdminRoute>
              } />
              <Route path="/blog-post-editor/:id" element={
                <AdminRoute><BlogPostEditor /></AdminRoute>
              } />
              <Route path="/admin/ai-settings" element={
                <AdminRoute><AISettings /></AdminRoute>
              } />
              <Route path="/conversation-data" element={
                <AdminRoute><ConversationData /></AdminRoute>
              } />
              <Route path="/metrics" element={
                <AdminRoute><Metrics /></AdminRoute>
              } />
              <Route path="/admin/reports" element={
                <AdminRoute><AdminReports /></AdminRoute>
              } />
              <Route path="/admin/test-pipeline" element={
                <AdminRoute><AdminTestPipeline /></AdminRoute>
              } />
              
              {/* Catch-all route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AnimatePresence>
          <ConditionalChatAgent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
